
import { Intent, Position, Size } from "@blueprintjs/core"
import { CSSProperties } from "react"
import { ANIMATED_STYLE } from "src/libs/interfaces/animated"
import { FaIcons, Color, CSSBorder, DIALOG_SIZE, HTML, Icon, ID, IMedia, IMenuItem, IRoutingData, SC, URL, VKVideoItem, WINDOW_WIDTH, HasTitle, HasId, POST_STATUS } from "src/libs/interfaces/layouts"
import IScalarForm, { SCALAR_TYPES, VerticalType } from "src/libs/scalars/interface/IScalarForm"
import { IMediaSizes, NAVIGATION_DESIGN_TYPE, NAVIGATION_POSITION_TYPE, NAVIGATION_SIZE_TYPE, NAVIGATION_THEME_TYPE, NAVIGATION_TYPE, PAGINATION_TYPE } from "src/libs/useful/gallery/Gallery"
import { IMarker } from "src/libs/yandexMap"
import { IUser } from "src/settings/zustand"

export interface ILanding {
    hideOriginalHeader?: boolean 
    background: ILandingSectionBackground
    delimiter: ILandingDelimiter
    modal: ModalContent
    sections: ILandingSection[]
    title: ILandingTitleProps
    description: ILandingTitleProps
    isLockAnimation?: boolean
    dataSize?: DIALOG_SIZE,
    defaultPadding?:SECTION_PADDING,
    fonts: string[]
    font_size?: number
    sectionClass?: string
    sectionRound?: number
    toUp: IToUp
    gap?: number
    size: DIALOG_SIZE | number
    categories?: ILandingCategory[]
    className?: string
    style?: CSSProperties
    css?: string[]
}
export interface ILandingTitleProps  extends IHasDimentions{
    className?: string 
    typography?: ITypography
    style?: CSSProperties
    beforeStyle?: CSSProperties
    afterStyle?: CSSProperties
    spanStyle?: CSSProperties
    before?: HTML
    after?: HTML
    align?: ALIGN
}

export interface ILandingCategory {
    id: ID
    title: HTML
    content?: HTML
    color?:Color
    icon?: string // FaIcons
    parentId?: ID
}

export interface ILandingDelimiter extends IHasDimentions, IAnimate {
    isDisabled?: boolean
    width?: number | string
    height?: number|string
    x?: number|string
    y?: number|string
    z?: number
    bottom?: number|string 
    className?: string
    style?: CSSProperties
    element?: JSX.Element
    bg?: Color
    edges?: ILandingDelimiterEdge[] 
}
export interface ILandingDelimiterEdge extends IHasDimentions {
    type: number
    fill?: Color
}

export interface ILandingSection extends IHasDimentions, IAnimate, IHasBackground, ITimelineCard {
    id: ID 
    background: ILandingSectionBackground
    data: ILandingContent[]
    isHidden?:boolean
    title: ILandingSectionTitle
    defaultTitle?: string
    description: ILandingSectionDescription
    title_container_style?: CSSProperties
    delimiter: ILandingDelimiter
    maxSectionID: ID
    maxFloatID: ID
    length: number
    height?: number | string
    width?: number | string
    overflow?: OVERFLOW_TYPE
    designType?: SECTION_DESIGN_TYPE
    composition: ISectionComposition
    horizontal_align?:ALIGN
    vertical_align?:ALIGN
    floats: ILandingFloat[]
    className?: string
    style?: CSSProperties
    containerStyle?: CSSProperties
    dstyle?: CSSProperties
    paddingTop?: SECTION_PADDING | number
    paddingBottom?: SECTION_PADDING | number 
    gap?: number
    isVertical?: boolean // designType = "tabs" 
    is_navigation?: boolean
    carousel_auto_height?: boolean
    carousel_nav?: NAVIGATION_TYPE
    carousel_nav_theme?: NAVIGATION_THEME_TYPE
    carousel_nav_size?: NAVIGATION_SIZE_TYPE 
    carousel_nav_type?: NAVIGATION_DESIGN_TYPE
    carousel_nav_position?: NAVIGATION_POSITION_TYPE 
    carousel_autoplay?: number //msec
    interaction_kind?: "hover" | "click"
    cardColors?: Color[]
    itemStyle?: CSSProperties // Carousel type only    
    tabs_design_type?: TAB_DESIGN_TYPE // Tabs type only
    haccordeon_design?: HAccordeonStyle
}


export interface ILandingCanvas extends Omit<ILandingContent, "title" | "description" | "maxSectionID" | "maxFloatID"> {
    floats: ILandingFloat[]
    values: any[]
    dimentions?: IScreenModes
    position: IScreenPosition
}

export interface ILandingSectionBackground {
    image?:URL | string
    image_opacity?: number
    image_mask?: string
    fillType?: BACKGROUND_FILL_TYPE
    effect?: BACKGROUND_EFFECT_TYPE
    parallax_speed?: number
    parallax_y_offset?: number
    style?: CSSProperties
    stack?:"image" | "color"
    color?: Color 
    color2?: Color 
    border?: CSSBorder
    dark?: Color 
    color_rotate?: number // 0-360 deg
    color_contrast?: number // 0-100 for mademoiselle
    color_size?: number // size for mademoiselle
    tile_opacity?: number
    tile_size_px?: number
    tile_position_x?: SCALAR_TYPES.CSS_MAGNITUDE
    tile_position_y?: SCALAR_TYPES.CSS_MAGNITUDE
    tile_repeat: "repeat" | "no-repeat"
    floats?: ILandingFloat[]
    opacity?:number
} 
export interface IScreenPosition {
    left?:    number | string
    right?:   number | string
    top?:     number | string
    bottom?:  number | string
    width?:   number | string
    height?:  number | string
    aspect?:  number
    rotate?:  number
    scale?:   number
    z?:       number
    skewX?:   number
    skewY?:   number
    style?:   CSSProperties
    spanStyle?: CSSProperties
    className?: ""
    data?:    any
    opacity?: number
}
export interface IHasDimentions {
    dimentions?: IScreenModes
    position: IScreenPosition
}
export interface ILandingFloat extends IHasDimentions, IAnimate {    
    id: ID 
    sections?: ILandingSection[]
    images?: URL[]
    text?: HTML
    overflow?: OVERFLOW_TYPE    
    isHidden?:boolean
    effect?: BACKGROUND_EFFECT_TYPE
    parallax_speed?: number
    parallax_y_offset?: number
    className?: string
    style?: CSSProperties 
    primitives?: ILandingPrimitive[]
    isCanvas?: boolean
}
export interface IScreenModes {
    [WINDOW_WIDTH.PANEL]?: IScreenPosition
    [WINDOW_WIDTH.LARGE]?: IScreenPosition
    [WINDOW_WIDTH.SCREEN]?: IScreenPosition
    [WINDOW_WIDTH.TABLET]?: IScreenPosition
    [WINDOW_WIDTH.MOBILE]?: IScreenPosition
    [WINDOW_WIDTH.SMALL]?: IScreenPosition
}
export interface ILandingSectionTitle extends IHasDimentions, IAnimate  {
    text: string  
    className?: string
    style?: CSSProperties  
    container_class_name?: string
    isHidden?: boolean
    align?: ALIGN,    
    beforeStyle?: CSSProperties
    afterStyle?: CSSProperties
    spanStyle?: CSSProperties
    before?: HTML
    after?: HTML
    typography?: ITypography
}
export interface ILandingSectionDescription extends IHasDimentions, IAnimate  {
    text: string 
    className?: string
    style?: CSSProperties 
    isHidden?: boolean
    typography?: ITypography
}
export interface ILandingPrimitive extends IHasDimentions, IAnimate {
    id: ID
    type?: LANDING_PRIMITIVE_TYPE | LANDING_TYPE_CONTENT | string
    data?: ILandingPrimitive
    fill?: Color
    isInline?:boolean
    isHidden?:boolean
    effect?: BACKGROUND_EFFECT_TYPE
    parallax_speed?: number
    parallax_y_offset?: number
    className?: string
    style?: CSSProperties,
    height?:number | string 
    [key:string]: any
}


export interface ISectionComposition {
    type: SECTION_PROPORTION_TYPE
    proportia: number[]
    columns: number
    isBlocked?:boolean
    textBefore?: string
    textAfter?: string
    container?: DIALOG_SIZE
    width?: number
    debug?: any
}

export interface IAnimate {
    animated?: ANIMATED_STYLE[]
    animated_delay?: number | string
}
export interface IHasBackground {
    smart_background?: ILandingSectionBackground
}

export interface IToUp extends IAnimate {
    isEnable?: boolean
    position: IScreenPosition
    place: Position
    design?: string | NAVIGATION_DESIGN_TYPE
    style: CSSProperties
    text_color?: Color
    font_size?: number
    bg?: Color
    border?: CSSBorder
    margin?: number
    className?: string 
}
 

export interface ILandingContent extends IHasDimentions, IAnimate, IHasBackground, HasTitle { 
    id: ID
    className?:string
    style?: CSSProperties
    containerStyle?: CSSProperties
    width?: string
    height?: string
    horizontal_align? : ALIGN
    vertical_align? : ALIGN  
    isHidden?: boolean
    data?: any,
    type:LANDING_TYPE_CONTENT
    text_color?:Color
    text_align?: ALIGN
    font?: string
    font_weight?: FONT_WEIGHT
    font_size?: number
    font_case?: FONT_CASE
    font_style?: FONT_STYLE
    line_height?: number
    letter_spacing?: number
    cardIcon?: keyof typeof FaIcons
    "data-primitive-type": string 
    dot_stroke_width?: number
    clickId?: ID
    [key: string]: any
    
}
export interface ImageContent extends ILandingContent {
    src?: URL
    src_type?: "div" | "img"
    original_size?: string
    background_position_x?: number
    hover_size?: number
    aspect?: number
    width_fixed?: string
    description?: HTML
    description_title?: HTML
    description_style?: CSSProperties
    description_class_name?: string,
    is_contrast_muar?: boolean
    exact_value?: number // persents
    link_url? : string
    link_target?: string
    link_class_name: string
    border?: CSSBorder
    type: LANDING_TYPE_CONTENT.IMAGE
}
export interface ThumbnailTileContent extends ILandingContent {
    images: string[] 
    gap?: number
    type: LANDING_TYPE_CONTENT.THUMBNAIL_TILE
}
// export interface HTMLContent extends ILandingContent {
//     text: string
//     columns?: number
//     gap?: number
//     max_length?: number
//     text_size?: TEXT_SIZE
//     text_family?: string
//     text_align?: ALIGN
//     text_color?:Color
//     type: LANDING_TYPE_CONTENT.HTML
// }
export interface OrderContent extends ILandingContent {
    order: number
    lehgtn?: number
    is_show_full?:boolean
    significant_zero?:boolean
    type: LANDING_TYPE_CONTENT.HTML
}
export interface ModalContent extends ILandingContent {
    content: DataContentContent | string
    size?: DIALOG_SIZE,
    type: LANDING_TYPE_CONTENT.MODAL
    dialogCSS?: CSSProperties
    contentCSS?: CSSProperties
    portalCSS?: CSSProperties
    footerCSS?: CSSProperties
    titleElementsCSS?: CSSProperties
    footerElementsCSS?: CSSProperties
    overlayType?: OVERLAY_TYPE.MODAL
}
export interface CanvasContent extends ILandingContent {
    type: LANDING_TYPE_CONTENT.CANVAS
}
export interface YamapContent extends ILandingContent {
    coordinates?: number[]
    markers?: IMarker[]
    type: LANDING_TYPE_CONTENT.YANDEX_MAP
}
export interface PersonalLinksContent extends ILandingContent {
    sc: SC
    type: LANDING_TYPE_CONTENT.PERSONAL_CARDS
}
export interface TagsContent extends ILandingContent {
    tags: ITagItem[]
    type: LANDING_TYPE_CONTENT.TAGS
}
export interface CheckBoxContent extends ILandingContent {
    tags: ITagItem[]
    type: LANDING_TYPE_CONTENT.CHECK_BOX
}
export interface PersonContent extends ILandingContent {
    user: IUser
    avatarWidth?: number,
    avatarHeight?: number,
    avatarClassName?: string
    isVertical?: boolean,
    border?: CSSBorder
    hover_size?: string
    type: LANDING_TYPE_CONTENT.PERSON
}
export interface VKVideoContent extends ILandingContent {
    src:URL
    btnWidth?:  number | string
    btnHeight?: number | string
    padding?:   number | string
    intent?:    Intent | string
    minimal?:   boolean
    fill?:      boolean
    image?:     URL
    label?:      string
    isModal?:   boolean
    isAutoMode?: boolean
    isPlayButton?: boolean
    onEnded?: () => void
}
export interface VKVideoPlaylistContent extends ILandingContent {
    id: ID
    items: VKVideoItem[]
    btnWidth?:  number | string
    btnHeight?: number | string
    padding?:   number | string
    intent?:    Intent | string
    minimal?:   boolean
    fill?:      boolean
    image?:     URL
    label:      string
    isModal?:   boolean
    isAutoMode?: boolean
    playerDesign: VIDEO_PAYER_DESIGN_TYPE
    descriptionAfter?: boolean // only VIDEO_PAYER_DESIGN_TYPE.HORIZONTAL_LIST
    isPlayButton?: boolean
    columnsCount?:number
    navigationPositionType?: NAVIGATION_POSITION_TYPE
    navigationSizeType?: NAVIGATION_SIZE_TYPE
    navigationThemeType?: NAVIGATION_THEME_TYPE
    navigationDesignType?: NAVIGATION_DESIGN_TYPE
    onEnded?: () => void
}
export interface DotsNavigationContent extends ILandingContent {
    type: LANDING_TYPE_CONTENT.DOTS_NAVIGATION
    items: DotNavigation[]
    color?:Color 
    borderColor?: Color
    borderWidth?: number
    borderRound?:number
    activeColor?: Color
    activeBorderColor?: Color
    activeBorderWidth?: number
    activeWidth?: number
    activeHeight?: number
    gap?: number
    align: ALIGN
    distance?: number
    tooltip?: boolean
    tooltipColor?: Color
    tooltipFontStyle?: CSSProperties
    design_type?: 0 | 1 | 2 | 3 | 4 | 5
    overlayType?: OVERLAY_TYPE.POPOVER
}
export interface DotNavigation {
    anchor: ID
    title: string
    isHidden?: boolean
}
export interface GalleryContent extends ILandingContent {
    items: ( IMedia | URL | string | Element | any )[]
    itemWidth:number | string
    itemHeight?:number | string
    count?: number | IMediaSizes
    autoplay?: number
    start?: number
    loop?:boolean
    navigation?:boolean
    pagination?:boolean
    padding?: number
    galleryType?: "image" | "div" | "span" | "element"
    navigationType?: NAVIGATION_TYPE
    navigationPositionType?: NAVIGATION_POSITION_TYPE
    paginationType?: PAGINATION_TYPE
    galleryContainerClassName?: string
}
export interface PassageContent extends ILandingContent {
    items: ( IMedia | URL | string | Element | any )[]
    itemWidth?:number | string
    itemHeight?:number | string
    gap: number
    hover_size: number
    src_type?: "div" | "img"
    galleryType?: "image" | "div" | "span" | "element"
    passageType: PASSAGE_TYPE
    galleryContainerClassName?: string
    border?: CSSBorder
}
export interface PassageSyncContent extends PassageContent {
    columns: number
    imageHeight: number
}
export interface TimeContent extends ILandingContent {
    type: LANDING_TYPE_CONTENT.TIME
    start_time?: Date
    finish_time?: Date    
    format?: string
    labelClassName?: string
    intent?: Intent
    round?: boolean
    minimal?: boolean
}
export interface TestinomialContent extends ILandingContent {
    
    type: LANDING_TYPE_CONTENT.TESTINOMIALS
}
export interface ZoomPanPinchContent extends ILandingContent {
    maxScale: number
    minScale: number 
    type: LANDING_TYPE_CONTENT.ZOOM_PANCH_PINCH
    
    incut: ILandingContent[]
    incutWidth?: SCALAR_TYPES.CSS_MAGNITUDE
    incutHeight?: SCALAR_TYPES.CSS_MAGNITUDE 
    incutBorder?: CSSBorder
}
export interface DividerContent extends ILandingContent {
    divider_type: DIVIDER_TYPE
    divider_width?: string | number
    divider_color?: Color
    type: LANDING_TYPE_CONTENT.DIVIDER 
}
export interface CardsContent extends ILandingContent, IAnimate {    
    columns?: number
    cards: ICard[]
    order: string[]
    orientation?: VerticalType
    data_type: CardDataType
    card_fields: CardFiled[]
    cards_count?: number
    cards_height?:number
    gap?:number
    hide_action_icon?: boolean
    decoration?: any
    clipping?:boolean
    vertical_align?: ALIGN
    cardTitleTypography?: ITypography
    cardStyle?: CSSProperties
    card_inner_style?: CSSProperties
    cross_icon?: CLOSE_ICON 
    cross_icon_style?: CSSProperties
    tail_type?: CARD_TAIL_TYPE
    tail_style?: CSSProperties
    tail_color_hover?: any
    is_empty_tail_hide?:boolean
    tension?: any
    border_radius: number | number[]
    box_shadow?: string 
    horizontal_grid_proportia?: number[]
    reverse_mounting?: boolean
    is_back_image?:boolean
    is_contrast_muar?:boolean
    back_image_vertical?: string|number
    exact_value: any
    back_image?: URL|string
    is_joint?: boolean
    back_opacity?:number
    back_color?: Color
    is_back_dissolution?: boolean
    color: Color
    lasy_load_behavior?: LEASE_LOAD_BEHAVIOR_TYPE    
    type: LANDING_TYPE_CONTENT.CARDS 
    feedType: SECTION_DESIGN_TYPE,
    is_pagination?: boolean
    carousel_nav_type?: NAVIGATION_DESIGN_TYPE
    carousel_nav_position?: NAVIGATION_POSITION_TYPE
    carousel_nav_size?: NAVIGATION_SIZE_TYPE
    carousel_nav_theme?: NAVIGATION_THEME_TYPE
    carousel_nav?: NAVIGATION_TYPE
    carousel_autoplay?: number //msec
    border: Border
    interaction_kind?: "hover" | "click"
    // animated?: ANIMATED_STYLE    
    // animated_delay?: CARD_ANIMATE_DELAY | string | number    
    design_type?: 0 | 1 | 2 | 3 
    thumbnail?: ImageContent
    isRightThumbnail?: boolean
    proportia?: number // aspect thumbnail per cards (default - 0.5)
} 
export enum CLOSE_ICON {
    CHEVRON="chevron-right",
    CARET="caret-right",
    TIMES="times",
    ARROW="arrow-right",
    ARROW_CIRCLE="arrow-alt-circle-right",
    ANGLE_DOUBLE="angle-double-right"
}
export interface Border {
    stroke?: Color
    strokeWidth?: number
    noseDistination?: SECTION_PROPORTION_TYPE 
    borderType?: BORDER_TYPE
    path?: ICurveDot[]
    isClosed?: true
    radius?: number | number[]
    [key: string]: any
}
export interface ICurveDot {
    x: number
    y: number
    left?:boolean
    right?:boolean
    top?:boolean
    bottom?:boolean
    type?: CURVE_TYPE
}
export interface WidgetContent extends ILandingContent {
    widgetId?: string
    attributes?: any 
    type: LANDING_TYPE_CONTENT.WIDGET
}
export interface HeaderContent extends ILandingContent {
    menu?: MenuContent
    logo?: LogoContent
    contacts?: ContactsContent 
    isOvertopSwicth?: boolean
    overtopMenu?: MenuContent
    overtopLogo?: LogoContent
    overtopContacts?: ContactsContent
    overtopStyle?: CSSProperties
    overtopClassName?: string
    overtopTypo?: ITypography
    overtopBlurDrop?: number
    overtopBackground?: Color
    vertical?: boolean
    orders?: [ LANDING_TYPE_CONTENT.MENU, LANDING_TYPE_CONTENT.LOGO, LANDING_TYPE_CONTENT.CONTACTS ]
} 

export interface ITypography {
    text_color?:Color
    text_align?: ALIGN
    font?: string
    font_weight?: FONT_WEIGHT
    font_size?: number
    font_case?: FONT_CASE
    line_height?: number
    letter_spacing?: number

}
export enum MOBILE_OPEN_TYPE {
    NONE="none",
    HAMBURGER_DRAWER="hamburger_drawer",
    HAMBURGER_POPOVER="hamburger_popover",
}
export interface MenuContent extends ILandingContent {
    items?: IMenuItem[] 
    menuId?: keyof IRoutingData
    design_type?: "iconic" | "inline"  
    mobileOpenType: MOBILE_OPEN_TYPE
    type: LANDING_TYPE_CONTENT.MENU
}
export interface CurveContent extends ILandingPrimitive {
    path: ICurveDot[]
    type: LANDING_TYPE_CONTENT.MENU
    borderRadius?: number
    strokeW?: number
    fill?: Color | string
    color?: Color | string
}
export interface FBGoodsContent extends Omit<CardsContent, "card_fields"> { 
    src: string 
    design_type?: 0 | 1 | 2 | 3 | 4
    card_fields: FBGoodsFields
    order: (keyof FBGoodsFields)[]
    type: LANDING_TYPE_CONTENT.FB_GOODS
}
export interface FBGoodsFields {     
    empty: EmptyCardField
    thumbnail?: avatarCardField 
    title: TitleCardField,    
    background? : ImageCardField 
    description?: TextCardField
    content?: TextCardField
    links?: PersonalLinksCardFiled
    divider?: DividerCardFiled    
    order?: OrderContent
    statistic_line?: StatisticLineCardField
    moreButton?: LinkCardFiled  
    person : PersonLineCardField,
    category_label?: CategoryLabelCardField
}

export interface LinksFeedContent extends Omit<ILandingContent, "title"> {
    items: ILink[]
    columns: number
    categoryId?: ID[]
    isCategoryFiltered?: boolean
    isOrder?: boolean
    isStatusFiltered?:boolean
    isTopPagination?:boolean 
    isAdd?: boolean
    isRating: boolean
}
export interface ILink {
    id?: ID
    url: URL
    title?: HTML
    description?: HTML
    image?: string
    keywords?: string | string[]
    status: POST_STATUS
    categories?: ILandingCategory[]
    categoryId?:ID[]
    rating?: any[]
}
export interface ITag extends HasId, HasTitle {
    color: Color
    description: HTML
}
export interface LogoContent extends Omit<ILandingContent, "title"> {
    src: URL 
    title?: HTML
    link_url?: URL | string
    link_target?: string
    description?: HTML 
    commentary?: HTML
    titleStyle?: CSSProperties
    descriptionStyle?: CSSProperties
    commentaryStyle?: CSSProperties
    vertical?: boolean
}

export interface InfoGraphiscContent extends Omit<CardsContent, "type"> {
    src: URL 
    silhouette: SILHOUETTE,
    title?: HTML
    description?: HTML 
    cards_width?: number
    dotSize: number
    size: number, 
    silhouette_gap: number,
    type: LANDING_TYPE_CONTENT.INFO_GRAPHICS
}
export interface TableContent extends ILandingContent {
    data: ITableRow[],
    threads: ITableRow,
    design?: " light" | "dark" | "success" | "primary" | "danger"
    striped?: boolean
    hover?: boolean
    bordered?: boolean
    small?: Size
}
export interface ITableRow extends HasId, IAnimate {
    cells: ITableRCell[]
    isHidden?: boolean
    scope: string
    colSpan?: number
    cellHeight?: number
    cellWidth?: number
}
export interface ITableRCell extends HasId {
    text: string | HTML
    cellHeight?: number
    cellWidth?: number
    color?:Color
    bg?: Color
    colSpan?: number
    rowSpan?: number
    border?: CSSBorder
}
export interface TimeContent extends ILandingContent {
    type: LANDING_TYPE_CONTENT.TIME
    start_time?: Date
    finish_time?: Date
    format?: string
    labelClassName?: string
    intent?: Intent
    round?: boolean
    minimal?: boolean
}
export interface TabsContent extends ILandingContent {
    type: LANDING_TYPE_CONTENT.TABS
    items: ITabContent[]
    vertical?: boolean
    tabs_design_type?: TAB_DESIGN_TYPE;
    tab_design?: ITabDesignStyle
}
export interface ITabDesignStyle { 
    tab_list_style?: CSSProperties
    tab_list_before_style?: CSSProperties
    tab_list_after_style?: CSSProperties
    tab_panel_style?: CSSProperties
    tab_panel_before_style?: CSSProperties
    tab_panel_after_style?: CSSProperties
    tab_style?: CSSProperties
    tab_before_style?: CSSProperties
    tab_after_style?: CSSProperties
    active_tab_style?: CSSProperties
    active_tab_before_style?: CSSProperties
    active_tab_after_style?: CSSProperties
    tab_indicator_style?: CSSProperties
}
export interface ContactsContent extends ILandingContent {
    design_type?: LINKS_DESIGN_TYPE
    items: IMenuContact[]
    carousel_count?: number | "auto"
    size?: number
}
export interface ButtonsContent extends ILandingContent   {
    items: ButtonContent[]
    icon?: string
    rightIcon?: string
    button_color?: Color
    border_width?: number
    labelStyle?: CSSProperties
    design_type?: LINKS_DESIGN_TYPE
}
export interface ButtonContent extends ILandingContent   {
    i?:number
    label?:string
    url?: URL
    design_type?: LINKS_DESIGN_TYPE
    type: LANDING_TYPE_CONTENT.BUTTON
    image?: URL | string
    imageHeight?: number
    imageWidth?: number
    icon?: string
    button_color?: Color
    border_width?: number
    labelStyle?: CSSProperties    
    children?: JSX.Element
    items?: ButtonContent[]
}
export interface IconContent extends ILandingContent   {
    src: string
    onClick?: () => void
    size?: number
    className?: string 
}
export interface DataContentContent extends ILandingContent   {
    data: ILandingContent[]
    type: LANDING_TYPE_CONTENT.DATA_CONTENT
}
export interface RandomContentContent extends ILandingContent   {
    data: ILandingContent[]
    type: LANDING_TYPE_CONTENT.RANDOM_CONTENT
    isNextButton: boolean
    nextButtonLabel?: HTML
}
export interface CategoryLabelContent extends ILandingContent   {
    category:ILandingCategory
    title: string
    type: LANDING_TYPE_CONTENT.MARRIAGE
}
export interface IMenuContact extends ButtonContent{ 
    sc?: SC 
    cf?: IContactFormLink 
    url?: string
    label?: HTML
    className?: string
    style?: CSSProperties
    image?: URL
    imageWidth?: number
    imageHeight?: number
}
export interface ITabContent { 
    title: string | JSX.Element
    id: ID,
    panel: ILandingContent[]
}
export interface IContactFormLink {
    label?: string
    cs?: SC
    contactFormSectionID: ID
    behovior?: "goto" | "modal"
}

export interface MarriageContent extends ILandingContent {
    design_type: 0 | 1 | 2 | 3 
    hover_size?: number
    items: [ MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, ]
    text?: HTMLContent
    type: LANDING_TYPE_CONTENT.MARRIAGE
}
export interface MarriageItem {
    src?: URL
    src_type?: "div" | "img"
    original_size?: string
    background_position_x?: number
    hover_size?: number
    width_fixed?: string
    description?: HTML
    description_style?: CSSProperties
    description_class_name?: string,
    is_contrast_muar?: boolean
    exact_value?: number // persents
    link_url? : string
    link_target?: string 
    style?: CSSProperties
    border?: CSSBorder
}
export interface MarriageText extends Omit<HTMLContent, "position" | "width" | "height" | "horizontal_align" | "vertical_align" > {

}
export interface VernissageContent extends ILandingContent {
    design_type: 0 | 1 | 2 | 3 
    hover_size?: number
    cover?: MarriageItem 
    coverImage?: string
    order?: DataContentContent
    items: [ MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, MarriageItem?, ]
    image0?: MarriageItem
    image1?: MarriageItem
    image2?: MarriageItem
    image3?: MarriageItem
    image4?: MarriageItem
    image5?: MarriageItem
    text?: HTMLContent
    coverTitle?: HTMLContent
    coverTitle2?: HTMLContent
    type: LANDING_TYPE_CONTENT.VERNISSAGE
    imageStyle?: CSSProperties
    imageBorder?: CSSBorder
}

export interface ContactFormContent extends ILandingContent {
    fields: ContactFormFields
    thumbnail?: URL 
    description?: HTML
    type: LANDING_TYPE_CONTENT.CONTACT_FORM
    isVerticalTitles?: boolean
    sendButton?: ButtonContent
    isOverlayButton?: boolean
    overlayType?: OVERLAY_TYPE
    overlayIcon?: string
    overlayIconSize?: number
    overlayBg?: Color
    overlayIconColor?:Color ,
    overlayBorder?: CSSBorder
    overlayButtonSize?: number
    overlayBacklightAnimation?: boolean
}
export interface ContactFormFields {
    [fieldName: string]: ContactFormField
}
export interface ContactFormField extends IScalarForm {

}

export interface HTMLContent extends ILandingContent {
    text:string
    text_size?: TEXT_SIZE 
    scroling?: SCROLLING_TYPE
    link_url?: string 
    link_target?: string 
    wrapped?: boolean
    type: LANDING_TYPE_CONTENT.HTML
    text_color?: Color
    text_family?: string 
    background?: Color 
    padding? : number
    incut?: DataContentContent
    incutWidth?: SCALAR_TYPES.CSS_MAGNITUDE
    incutHeight?: SCALAR_TYPES.CSS_MAGNITUDE
    incutPosition?: "left" | "right"
    incutBorder?: CSSBorder
}
export interface TwirlTextContent extends ILandingContent {
    text:string
    text_size?: TEXT_SIZE 
    twirl_text: HTML[]
    wrapped?: boolean
    type: LANDING_TYPE_CONTENT.TWIRL_TEXT 
    text_color?: Color
    twirl_text_color?: Color | Color[]
    twirl_tail_delay?: Number
    text_family?: string 
    background?: Color
    padding? : number 
}
export interface WriterContent extends ILandingContent {
    text:string  
    wrapped?: boolean
    type: LANDING_TYPE_CONTENT.WRITER
    writerType: WriterType 
    background?: Color
    padding? : number 
    speed?: number
    variant?: number
}
export interface ICard extends IAnimate, HasTitle {
    i:number
    id:ID 
    orientation?: VerticalType
    data_type: CardDataType
    card_fields: CardFiled[]
    cards_count?: number
    cards_height?:number
    gap?:number
    hide_action_icon?: boolean
    decoration?: any
    clipping?:boolean
    vertical_align?: ALIGN
    card_style?: CSSProperties
    card_inner_style?: CSSProperties
    tail_type?: CARD_TAIL_TYPE
    taIl_style?: CSSProperties
    tail_color_hover?: any
    is_empty_tail_hide?:boolean
    tension?: any
    border_radius: number | number[]
    box_shadow?: string
    random_rotation?: number
    horizontal_grid_proportia?: number[]
    reverse_mounting?: boolean
    is_back_image?:boolean
    is_contrast_muar?:boolean
    back_image_vertical?: string|number
    value: any[]
    back_image?: URL|string
    back_color?: Color
    is_back_dissolution?: boolean
    is_joint?: boolean
    back_opacity?:number
    color: Color
    lease_load_behavior?: LEASE_LOAD_BEHAVIOR_TYPE
    style?: CSSProperties
    cardStyle?: CSSProperties
    className?: string     
    before?: HTML
    after?: HTML
    cardTitle?: HTML
    cardDescription?: HTML
    cardIcon?: Icon
    cardColor?:Color
    type: LANDING_TYPE_CONTENT.CARDS
    borderType?: BORDER_TYPE
    isHidden?: boolean
}
export interface ITimelineCard extends ICard {    
    is_two_side?: boolean 
    dots: ITimelineDot 
}
export interface ITimelineDot {
    type?: TIMELINE_DIAGRAM_TYPE
    color?:  Color
    perimeter?: number
    weight?: number
    fontSize?: number
    fontColor?: Color
    radius?: number | string
    significant_zero?:boolean
    shift?: number
    length?: number
    amplitude?: number  // 0-2000
    phase?: number  // 0-1
    stroke?: number 
    fill?: Color
    stroke_width?: number
    svg_style?: CSSProperties
    dot_style?: CSSProperties
    active_dot_style?: CSSProperties
    dot_before_style?: CSSProperties
    dot_after_style?: CSSProperties
    title_style?: CSSProperties
    isSlide?: boolean //Показывать в карусели. Не реализовано
    slidesPerScreen?: number
    dot_stroke_width?: number,
    title?: string
    defaultTitle?: string
}
export type CardFiled = {
    id: ID
    type: SCALAR_TYPES | CARD_FIELD_TYPE
    pseudonym? : string
    style?: CSSProperties
    className? : string
    parallax_speed?: number
    parallax_y_offset?: number
    effect?: BACKGROUND_EFFECT_TYPE
    isHidden?: boolean      
    before?: HTML
    after?: HTML      
    gridColumnCroupID?: number
    gridRowCroupID?: number 
    [key: string] : any
}

export interface PersonsContent extends Omit<CardsContent, "card_fields"> {    
    design_type?: 0 | 1 | 2 | 3 | 4
    card_fields: PersonalFields
    order: (keyof PersonalFields)[]
    type: LANDING_TYPE_CONTENT.PERSONAL_CARDS
}
export interface PersonalFields {
    empty: EmptyCardField
    avatar?: avatarCardField
    avatarMedallion?: avatarMedallionCardField
    display_name?: TextCardField
    last_name?: TextCardField
    first_name?: NameCardField
    description?: TextCardField
    links?: PersonalLinksCardFiled
    divider?: DividerCardFiled
    statistic_line?: StatisticLineCardField
    moreButton?: LinkCardFiled
}
export interface EmptyCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.EMPTY 
}
export interface avatarCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.IMAGE 
}
export interface avatarMedallionCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.MEDALLION 
}
export interface NameCardField extends CardFiled { 
    type: SCALAR_TYPES.TITLE 
}
export interface TitleCardField extends CardFiled { 
    type: SCALAR_TYPES.TITLE 
} 
export interface TextCardField extends CardFiled { 
    type: SCALAR_TYPES.STRING 
} 
export interface PersonalLinksCardFiled extends CardFiled { 
    type: CARD_FIELD_TYPE.PERSONAL_LINKS 
}
export interface PersonLineCardField extends CardFiled {
    user: IUser
    type: CARD_FIELD_TYPE.PERSON
}
export interface CategoryLabelCardField extends CardFiled {
    category: ILandingCategory
    title: string
    type: CARD_FIELD_TYPE.PERSON
}
export interface StatisticLineCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.STATISTIC_LINE 
}
export interface DividerCardFiled extends CardFiled { 
    type: CARD_FIELD_TYPE.DIVIDER 
}
export interface LinkCardFiled extends CardFiled { 
    type: CARD_FIELD_TYPE.INNER_LINK | CARD_FIELD_TYPE.OUTER_LINK
}
export interface TimeCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.TIME
}
export interface MedallionCardField extends CardFiled { 
    isSquare?: boolean
    type: CARD_FIELD_TYPE.MEDALLION
}

export interface FeaturesContent extends Omit<CardsContent, "card_fields"> {
    card_fields: PersonalFields
    order: (keyof PersonalFields)[]
    type: LANDING_TYPE_CONTENT.PERSONAL_CARDS
} 
export interface FeaturesFields {
    icon?: IconCardField
    iconrMedallion?: IconMedallionCardField
    title?: NameCardField 
    description?: TextCardField
    links?: PersonalLinksCardFiled
    divider?: DividerCardFiled
    moreButton?: LinkCardFiled
    time?: TimeCardField
}

export interface ShedulesContent extends Omit<CardsContent, "card_fields"> {
    card_fields: SheduleFields
    order: (keyof SheduleFields)[]
    type: LANDING_TYPE_CONTENT.SHEDULE_CARDS
}
export interface SheduleFields {
    order?: OrderCardField
    thumbnail?: avatarCardField 
    title?: TextCardField
    eventTypeName?: NameCardField
    status?: NameCardField
    description?: TextCardField
    links?: PersonalLinksCardFiled
    divider?: DividerCardFiled
    moreButton?: LinkCardFiled
}

export interface ImageCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.IMAGE 
} 
export interface IconCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.IMAGE 
}
export interface IconMedallionCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.MEDALLION 
}
export interface OrderCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.ORDER 
}
export interface CanvasCardField extends CardFiled { 
    type: CARD_FIELD_TYPE.MEDALLION     
    groups: CardFiled[]
}

export interface TilineField extends CardFiled {
    
}

export interface ICardFieldValue {
    id: ID
    card_fields_style?: CSSProperties
    style?: CSSProperties
    className?: string
    data: ICardFieldData | any
    card_field_type?: string
}
export interface ICardFieldData {
    [key:string]: any
}
export interface ICardFieldPersonalLinks extends ICardFieldData {
    type: LINKS_DESIGN_TYPE
}
export interface ICardFieldDivider extends ICardFieldData {
    type: DIVIDER_TYPE
}
export interface ICardFieldOuterLink extends ICardFieldData {
    url: URL
    label: string | JSX.Element
}
export interface ICardFieldInnerLink extends ICardFieldData {
    url: string
    label: string | JSX.Element
}
export interface ITagItem {
    id: ID,
    checked: boolean
    title: string
    round?: boolean
}

export interface ISliderItem {
    id: ID,
    checked?: boolean
    value: number
    title: string
}
export interface CardDataType {

} 
export interface IExtendedParams {
    landing_id: ID
    land_id?: ID
    local?: boolean
    isEdit?: boolean
}
export interface IContentSelect {
    id: ID
    sectionId: ID
    type: string
    selectType: SELECT_TYPE
    bound: Partial<DOMRect>
}

export interface HAccordeonStyle {
    cover_width?:number
    label_size?:number
    icon_size?:number
    isOrder?: boolean
    isNoBackground?: boolean
    cardColors?: Color[]
    container?:CSSProperties
    card?: CSSProperties
    card_open?: CSSProperties
    card_cover?: CSSProperties
    card_cover_before?: CSSProperties
    card_cover_after?: CSSProperties
    card_label?: CSSProperties
    card_icon?: CSSProperties
    content_container?: CSSProperties
    content_container_before?: CSSProperties
    content_container_after?: CSSProperties
}
export interface ICurrentEditType {
    currentEditType: SELECT_TYPE
    sectionId: ID
    order?: number
    [key: string]: any
}
export interface IHistoryStep {
    type: HISTORY_TYPE,
    data: any,
    prevData: any,
    title?: string
}
export enum TAB_DESIGN_TYPE {
    NONE="none",
    PORTHOLE="porthole",
    CAPSULE="capsule",
    ARCHIVE="archive",
    ANCHORS="anchors",
    MODERN="modern",
    FLAP="flap",
    O_LA_LA="o-la-la",
    PITAYA="pitaya",
    PITAYA2="pitaya2",
    FOLDER="folder",
    DA_VINCI="da-vinci",
    JNT="jnt",
    JNT_LIMEGREEN="jnt-limegreen",
    JNT_CAPSULE="jnt-capsule",
    SORAX="sorax",
    SOAP="soap",
    CASE="case",
}
export enum HISTORY_TYPE {
    SECTION="section",
    SETTINGS="settings",
    FULL="full", 
    SECTIONS_ORDER="sectionsOrder"
}

export enum LANDING_EDIT_MODE {
    // STANDARD="standard",
    EDIT="edit",
    TRANSFORM="transform",
    INNER="inner",
    DRAG="drag",
    PRIMITIVES="primitives",
    // EXTENDED="extended"
}
export enum WriterType {
    PUSHKIN="pushkin",
    SCRAMBLE="scramble",
    JUMPER="jumper"
    
}
export const JUMPER_TYPES: any[] = [
    { id:"jump-letter-up", title:"Jump letters up" }, 
    { id:"jump-letter-left", title:"Jump letters left" }, 
]
// export const editModes: any = {
//     [LANDING_EDIT_MODE.STANDARD]: {
//         title: "Standard edit mode",
//         description: "Only the necessary tools",
//         icon: "fas fa-pen-nib",
//     },
//     [LANDING_EDIT_MODE.EXTENDED]: {
//         title: "Extended edit mode",
//         description: "Maximum control and visual editing tools",
//         icon: "fas fa-wrench",
//     },
// }

export enum BACKGROUND_FILL_TYPE {
    PLAIN="plain",
    SMART="smart",
    NOISE="noise",
    MADEMOISELLE="mademoiselle",
    ROADS="roads",
    HOLOGRAM="hologram",
    BOKE="boke",
    HEXAGONS="hexagons",
    FOLDS="folds",
    NONE="none"
}
export enum BACKGROUND_EFFECT_TYPE {
    VERTICAL_PARALLAX="vparallax",
    FIXED="fixed",
    NONE="none",
}
export enum ALIGN {
    START="start",
    MIDDLE="middle",
    END="end",
    FULL="full"
}

export enum FONT_WEIGHT {
    THIN="100",
    LIGHT="200",
    NORMAL="400",
    MEDIUM="500",
    SEMIBOLD="600",
    BOLD="700",
    EXTRA_BOLD="800",
    BLACK="900",
}

export enum FONT_CASE {
    NONE="none",
    LOWERCASE="lowercase",
    UPPERCASE="uppercase",
    CAPITALIZE="capitalize",
}

export enum FONT_STYLE {
    NONE="none",
    ITALIC="italic", 
}

export const TEXT_ALIGN = {
    [ALIGN.START]: "left",
    [ALIGN.MIDDLE]: "center",
    [ALIGN.END]: "right",
    [ALIGN.FULL]: "justify",
}
export const ALIGN_DATA = {
    [ALIGN.START]: {
        title: "Left",
        icon: 'align-left',
        command: "justifyLeft",
        className: "justify-content-start",
        align: "left"
    },
    [ALIGN.MIDDLE]: {
        title: "Center",
        icon: 'align-center',
        command: "justifyCenter",
        className: "justify-content-center",
        align: "center"
    },
    [ALIGN.END]: {
        title: "Right",
        icon: 'align-right',
        command: "justifyRight",
        className: "justify-content-end",
        align: "right"
    },
    [ALIGN.FULL]: {
        title: "Full",
        icon: 'align-justify',
        command: "justifyFull",
        className: "justify-content-full",
        align: "justify"
    },
}
export enum SCROLLING_TYPE {
    AUTO="overflow-auto",
    Y="overflow-y-auto",
    X="overflow-x-auto",
    NONE="overflow-visible",
}
export enum TEXT_SIZE {
    SMALL= 0.8,
    NORMAL=1,
    LARGE=1.4,
    GIANT=2
}
export enum SECTION_PROPORTION_TYPE {
    TOP="top",
    LEFT="left",
    BOTTOM="bottom",
    RIGHT="right",
    NONE="none"
}
export enum BORDER_TYPE {
    NONE="",
    NOSE="nose",
    CURVE="curve",
}
export enum CURVE_TYPE {
    M="M",
    L="L",
    S="S",
    C="C",
    Z="Z",
}

export enum OVERFLOW_TYPE {
    HIDDEN  = "hidden",
    // HIDDEN_BOTTOM = "hidden-bottom",
    // HIDDEN_TOP = "hidden-top",
    VISIBLE = "visible"
}

export enum LANDING_PRIMITIVE_TYPE {
    RECTANGLE   = "rectangle",
    OVAL        = "oval",
    STAR        = "star",
    HEART       = "heart",
    ORDER       = "order",
    LINE        = "line",
    CURVE       = "curve",
    //PIN         = "pin",
}
export const LANDING_PRIMITIVE_PIN = "pin"


export enum PE_LANDING_CONTAINER_TYPE {
    "DataContext",
    "carousel", 
    "columns", 
    "rows", 
    //"accordeon",
    //"haccordeon",
    "accordeon_sections",
    //"cards",
    //"Card",
    //"CardField", 
    "zoom_pan_pinch",
    "Dilimiter",
    "include_section", 
}

export enum CARD_FIELD_TYPE {
    BACKGROUND="background",
    EMPTY="empty",
    DIVIDER="divider",
    CROPPED_DIVIDER="cropped_divider",
    // SECTION="section",
    PRICE="price",
    TITLE="title",
    MEDALLION="medallion",
    IMAGE="image", 
    OUTER_LINK="outer_link",
    INNER_LINK="inner_link",
    BUTTONS="buttons",
    PERSONAL_LINKS="personal_link",
    TAGS="tags",
    CHECK_BOX="checkbox",
    STATISTIC_LINE="statistic_line",
    CANVAS="canvas",
    SPEECH_BUBBLE="speech_bubble",
    PERSON="person",
    ORDER="order",
    TIME="time",
    ICON="icon",
    LOGO="logo",
    CATEGORY_LABEL="category_label",
}

export const DEFAULT_SECTION_PADDING = 50
export enum SECTION_PADDING {
    NULL=0,
    HAIR=1,
    SMALL=20,
    MEDIUM=DEFAULT_SECTION_PADDING,
    NORMAL=75,
    LARGE=125,
    GIANT=200
}

export enum CARD_TAIL_TYPE {

}
export enum SECTION_DESIGN_TYPE {
    FEED="feed",
    COLUMN="column",
    CAROUSEL="carousel",
    VERTICAL_CAROUSEL="vertical_carousel",
    ACCORDEON="accordeon",
    HACCORDEON="horizontal_accordeon",
    TABS="tabs",
    VERTICAL_TABS="vertical_tabs",
    // VROLL="vertical_roll",
    // HROLL="horizontal_roll",
    VTIMELINE="vertical_timeline",
    HTIMELINE="horizontal_timeline", 
}

export enum CARD_ANIMATE_DELAY {
    NONE="",
    WATERFALL="waterfall",

}

export enum LEASE_LOAD_BEHAVIOR_TYPE {
    IN_ORDER="in_order",
    RANDOM="random",
    ARK="ark",
    CANCAVE="cancave",
    CHESS="chess",
}

export enum DIVIDER_TYPE {
    NONE="none",
    LINE="line",
    BOLD_LINE="bold_line",
    K="k",
    EXCLAMATION_SIGN="exclamation_sign",
    SOFT_SIGN="soft_sign",
    DOT="dot",
}

export enum LINKS_DESIGN_TYPE {
    SQUARE="square",
    ROUND="round",
    ROUND_OUTLINE="round_outline",
    ROUND_OUTLINE_BW="round_outline_bw",
    MINIMAL="minimal",
    OUTLINE="outline",
    OUTLINE_BW="outline_bw",
    SMALL="small",
    SMALL_ROUND="small_round",
    SMALL_MINIMAL="small_minimal",
    SMALL_OUTLINE="small_outline",
    SMALL_OUTLINE_BW="small_outline_bw",
} 
export enum VIDEO_PAYER_DESIGN_TYPE {
    HORIZONTAL_LIST="horizontal_list",
    VERTICAL_LIST="vertical_list",
    CAROUSEL="carousel",
    CAROUSEL_ICONS="carousel_icons",
    TILE="tile",
}
export enum CROPPED_DIVIDER_TYPE {
    NONE="none",
    WAVE_OUTLINE="wave_outline",
    WAVE_OUTLINE_REVERSE="wave_outline_reverse",
}
export const CARD_SEPARATOR = "=====cardSeparator======="
export const CARD_SEPARATOR_2 = "=====cardSeparator_2======="
export const CARD_SEPARATOR_3 = "=====cardSeparator_3======="

export enum FONT_SIZES_NAMES {  //, , , , , , 
    MICRO="micro", 
    SMALL="small", 
    NORMAL="medium",
    LARGE="large", 
    GIANT="giant",
    SUPER="super",
} 
export enum OVERLAY_TYPE {
    INLINE="inline",
    POPOVER="popover",
    MODAL="modal",
}
export enum LANDING_TYPE_CONTENT {
    HTML ="html",
    CANVAS="canvas",
    DIVIDER="divider",
    IMAGE="image", 
    WIDGET="pe_widget",
    CARDS="cards",
    PERSONAL_CARDS="personal_cards",
    FEATURES_CARDS="features_cards",
    TAGS="tags",
    CHECK_BOX="checkbox", 
    PERSON="person",
    TESTINOMIALS="testinomials",
    YANDEX_MAP="ymap",
    HEADER="header",
    LOGO="logo",
    CONTACTS="contacts",
    MENU="menu",
    BUTTON="button",
    CURVE="curve", 
    TIME="time",
    THUMBNAIL_TILE="thumbnail_tile",
    TABLE="table",
    VK_VIDEO="vk_video",
    VK_VIDEO_PLAYLIST="vk_video_playlist",
    GALLERY="gallery",
    ZOOM_PANCH_PINCH="zoom_pan_pinch",
    ORDER="order",
    SHEDULE_CARDS="shedules_cards",
    CONTACT_FORM="contact_form",
    MARRIAGE="marriage",
    VERNISSAGE="vernissage",
    ICON="icon",
    BUTTONS="buttons",
    TABS="tabs",
    FB_GOODS="fb_goods",
    CATEGORY_LABEL= "category_label",
    DOTS_NAVIGATION="dots_navigation",
    DATA_CONTENT="data_content",
    RANDOM_CONTENT="random_content",
    INFO_GRAPHICS="info_graphics",
    USER="user",
    PASSAGE="passage",
    WRITER="writer",
    TWIRL_TEXT="twirl_text",
    HEADER_ORDER="header_order",
    LINKS_FEED="links_feed", 
    MODAL="modal",
    TOOLTIP="tooltip",

    NONE="",
}

export enum PASSAGE_TYPE {
    SYNCHRONOUS="synchronous",
    HORIZONTAL="horizontal",
    MASONRY="masonry",
    SQUARE="square",

}

export enum TIMELINE_DIAGRAM_TYPE {
    SINUSOID="sinusoid",
    DIAGONAL="diagonal",
    BREAK_LINE="break_line",
    CONCERTINO="concertino",
    GALLERY="gallery"
}

export enum SELECT_TYPE {
    NONE="none",
    SECTION="section",
    CONTENT="content",
    FLOAT="float",
    TITLE="title",
    DESCRIPTION="description",
    DELIMITER="delimiter",
    BACKGROUND="background",
    CARD_FIELD_HTML="card_field_html",
    CARD_FIELD_TITLE="card_field_title",
    LINK_ITEM="link_item",
    PRIMITIVE="primitive",
    TABLE_TD="table_td",
    TABLE_THREAD="table_thread",
    HORIZONTAL_TAB="horizontal_tab",
    VERTICAL_TAB="vertical_tab",
    SECTION_TIMELINE="section_timeline",
    GALLERY="gallery",
    ACCORDEON="accordeon",
    HACCORDEON="horizontal_accordeon",
    VERTICAL_TIMELINE="vertical_timeline",
    LINKS_FEED="link_feed",
}

export enum SELECT_CURSOR {
    CARDS="cards",
    SECTION="section"
}

export enum SILHOUETTE {
    CHESS_HORSE="chess-horse",
    CHESS_KING="chess-king",
    CHESS_QUEEN="chess-queen",
    CHESS_PAWN="chess-pawn",
    CROWN="crown",
    CAT="cat",
    CAT2="cat-2",
    DOG="dog",
    DOVE="dove",
    EAGLE="eagle",
    HANDS="hands",
    HANDS2="hands-2",
    HANDS_OK="hands-ok",
    HANDS_VICTORY="hands-victory",
    MAN="man",
    WOMAN="woman",
    WORK_WOMAN="work-woman",
    SOURCE="source",
}

export enum BUNCH {
    LAYOUT="layout",
    TEXT="texts",
    IMAGES="images",
    GALLERIES="galleries",
    WIDGETS="widgets",
    PERSON="person",
    CARDS="cards",
    FEATURES_CARDS="features",
    TEAM="teams",
    TESTINOMIALS="testinomials",
    SHEDULE_CARDS="shedules",
    MAPS="maps",
    HEADERS="navigation",
    TABLES="tables",
    VIDEO="video",
    CONTACT_FORMS="contact_forms",
    BUTTONS="buttons",
    INFO_GRAPHICS="info_graphics",
    OVERLAY="overlay",
}

export const ScreenPlace: Position[] = [
    Position.RIGHT_BOTTOM,
    Position.BOTTOM,
    Position.LEFT_BOTTOM,
    Position.LEFT,
    Position.LEFT_TOP,
    Position.TOP,
    Position.TOP_RIGHT,
    Position.RIGHT,
]

export const HACCORDEON_BUTTON_WIDTH = 110
export const CARD_NOSE_SHIFT_Y = 100
export const NOSE_WIDTH = 8
export const CARD_TYPES: string[] = [ 
    LANDING_TYPE_CONTENT.CARDS,
    LANDING_TYPE_CONTENT.FEATURES_CARDS,
    LANDING_TYPE_CONTENT.TESTINOMIALS,
    LANDING_TYPE_CONTENT.SHEDULE_CARDS,
    LANDING_TYPE_CONTENT.FB_GOODS,
    LANDING_TYPE_CONTENT.INFO_GRAPHICS,
    LANDING_TYPE_CONTENT.PERSONAL_CARDS,
]
export const SPEECH_NOSE_HEIGHT = 18

export const defaultLanding = (): ILanding => {
    return {
        delimiter: {} as ILandingDelimiter,
        title: {} as ILandingTitleProps,
        description: {} as ILandingTitleProps, 
        sections: [] as ILandingSection[],
        background: {} as ILandingSectionBackground,
        fonts: [ "Open Sans" ],
        gap: 10,
        sectionRound: 0,
        size: DIALOG_SIZE.FULL,
        dataSize: DIALOG_SIZE.NORMAL,
        toUp: {} as IToUp
    } as ILanding
}



export const defaultPosition = ( isClearPosition: boolean = false ): IScreenPosition => {
    return {
        top:0,
        left:0,
        width:  isClearPosition? 0 : "100%",
        height: isClearPosition? 0 : "100%",
        opacity:1,
        scale:1,
        rotate: 0,
        style: {},
        className: "",
        data: {}
    }
}

export const defaultPrimitive = (): ILandingPrimitive => {
    return {
        id: Math.random(),
        position: defaultPosition(),
        bg: "yellow",
        effect: BACKGROUND_EFFECT_TYPE.NONE,
        className: "",
        style: {}
        
    } as ILandingPrimitive
}

export const defaultTimelineDots = (): ITimelineDot => {
    return {
        amplitude:0,
        phase:0,
        perimeter: 30,
        stroke_width:0,
        stroke:0,
        svg_style:{},
        fill: "red"
    }
}